/**
 * Property — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/property.schemas.ts`
 * imports these to register the routes, and `apps/landlord/src/lib/portfolio.api.ts`
 * derives its create-property request and response types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   POST /landlord/api/properties   (CreatePropertyRequest request body;
 *                                    CreatePropertyResponse 201 body)
 */
import { z } from 'zod';
import { isoDateTimeString } from '../shared/date.js';
import { CreateUnitRequestSchema } from './unit.js';
/** Property status */
export const propertyStatusSchema = z.enum(['active', 'inactive', 'deleted']);
/** Property type (main categorization) */
export const propertyTypeSchema = z.enum(['single_family', 'multi_family']);
/** Property sub-type (optional additional categorization) */
export const propertySubTypeSchema = z.enum([
    'apartment',
    'condo',
    'townhouse',
    'commercial',
    'mixed_use',
    'other',
]);
// ---------------------------------------------------------------------------
// Create property — POST /landlord/api/properties (request body)
// ---------------------------------------------------------------------------
/**
 * The Unit's address and details, sent with a single_family create: the Unit is
 * created in the same request. Its propertyId comes from the new Property and its
 * unitNumber is always DEFAULT, so neither is accepted here.
 */
export const createPropertyUnitSchema = CreateUnitRequestSchema.omit({ propertyId: true, unitNumber: true });
export const createPropertyRequestSchema = z
    .object({
    portfolioId: z.uuid('Invalid UUID format').describe('Portfolio ID (UUID v4)'),
    name: z
        .string()
        .min(1, 'Name is required')
        .max(255, 'Name must be 255 characters or less')
        .describe('Property name'),
    propertyType: propertyTypeSchema.describe('Property type: single_family or multi_family'),
    subType: propertySubTypeSchema
        .optional()
        .describe('Property sub-type: apartment, condo, townhouse, commercial, mixed_use, other'),
    status: propertyStatusSchema.default('active').describe('Property status: active, inactive, or deleted'),
    metadata: z.record(z.string(), z.unknown()).optional().describe('Custom metadata object'),
    unit: createPropertyUnitSchema
        .optional()
        .describe("The Unit's address and details — required for single_family, rejected otherwise"),
})
    .superRefine((data, ctx) => {
    if (data.propertyType === 'single_family' && data.unit === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['unit'],
            message: 'Unit address is required for a single-family property',
        });
    }
    if (data.propertyType !== 'single_family' && data.unit !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['unit'],
            message: 'Unit details are only accepted for a single-family property',
        });
    }
})
    .describe('Create property request payload');
// ---------------------------------------------------------------------------
// Create property response — POST /landlord/api/properties (201 body)
// ---------------------------------------------------------------------------
const uuid = z.uuid('Invalid UUID format');
/** Property details, as the property routes serialize a Property */
export const propertyResponseSchema = z
    .object({
    id: uuid.describe('Property ID (UUID v4)'),
    portfolioId: uuid.nullable().describe('Associated portfolio ID'),
    name: z.string().describe('Property name'),
    propertyType: z.string().describe('Property type: single_family or multi_family'),
    status: z.string().describe('Property status: active, inactive, or deleted'),
    metadata: z.record(z.string(), z.unknown()).describe('Custom metadata object'),
    defaultDepositAccountId: uuid.nullable().optional().describe('Default ACH deposit account ID'),
    createdAt: isoDateTimeString.describe('Creation timestamp (ISO 8601)'),
    updatedAt: isoDateTimeString.describe('Last update timestamp (ISO 8601)'),
})
    .describe('Property details');
/** Unit details, as the property routes serialize a Unit */
export const unitResponseSchema = z
    .object({
    id: uuid.describe('Unit ID (UUID v4)'),
    propertyId: uuid.describe('Associated property ID (UUID v4)'),
    unitNumber: z.string().nullable().describe('Unit number or identifier'),
    address1: z.string().describe('Primary address line'),
    address2: z.string().nullable().describe('Secondary address line'),
    city: z.string().describe('City name'),
    state: z.string().describe('State or province code'),
    zipCode: z.string().describe('Postal code'),
    bedrooms: z.number().int().nullable().describe('Number of bedrooms'),
    bathrooms: z.string().max(5).nullable().describe('Number of bathrooms'),
    sqft: z.number().int().nullable().describe('Square footage'),
    status: z.string().describe('Unit status'),
    metadata: z.record(z.string(), z.unknown()).describe('Custom metadata object'),
    createdAt: isoDateTimeString.describe('Creation timestamp (ISO 8601)'),
    updatedAt: isoDateTimeString.describe('Last update timestamp (ISO 8601)'),
})
    .describe('Unit details');
/** Create property response: the new Property, and its Unit when the Property is single_family */
export const createPropertyResponseSchema = z
    .object({
    property: propertyResponseSchema.describe('Created property details'),
    unit: unitResponseSchema.optional().describe('Auto-created unit (single-family properties only)'),
})
    .describe('Create property response');
//# sourceMappingURL=property.js.map